import { FilingFrequency, TaxType } from '@/types/filings';
import type { BillingPlanEnum } from '@/types/organizations';
import { CountryCode } from './location';
import { ValueOf } from './utils';

export enum RegistrationsRegimeEnum {
  STANDARD = 'STANDARD',
  SIMPLIFIED = 'SIMPLIFIED',
}

export enum ChangeRegimeStatusEnum {
  REQUESTED = 'REQUESTED',
  APPROVED = 'APPROVED',
  DONE = 'DONE',
  ACKNOWLEDGED = 'ACKNOWLEDGED',
}

export const RegistrationStatus = {
  REGISTERED: 'REGISTERED',
  PROCESSING: 'PROCESSING',
  UNREGISTERED: 'UNREGISTERED',
  DEREGISTERING: 'DEREGISTERING',
  DEREGISTERED: 'DEREGISTERED',
  VALIDATING: 'VALIDATING',
  AWAITING_CLARIFICATION: 'AWAITING_CLARIFICATION',
} as const;

export enum RegistrationTypeEnum {
  EU_OSS = 'EU_OSS',
  OTHER = 'OTHER',
}

export const RegistrationCategory = {
  REGULAR: 'REGULAR',
  IMPORTED: 'IMPORTED',
  DEREGISTRATION: 'DEREGISTRATION',
} as const;

export enum OSSScheme {
  UNION = 'UNION',
  NON_UNION = 'NON_UNION',
  IOSS = 'IOSS',
}

export type Registration = {
  status: ValueOf<typeof RegistrationStatus>;
  country_code: string;
  state_code: string;
  state_name: string;
  registration_date: string | null;
  registration_email: string | null;
  filing_frequency: ValueOf<typeof FilingFrequency>;
  filing_days: number;
  tax_type?: TaxType;
  id: string;
  imported: boolean;
  username: string;
  create_filings_from: string | null;
  billingPlan: BillingPlanEnum;
  has_all_credentials?: boolean;
  amount_fees: string;
  vda: boolean;
  comment?: string;
  registrations_regime?: RegistrationsRegimeEnum;
  change_regime_status?: ChangeRegimeStatusEnum;
  third_party_enabled?: boolean;
  /** True when first filing period was current at import and still in validation (KA-1643). */
  current_period_validation_priority?: boolean;
  /** True while a PROCESSING registration is pre-collecting (no ops task). */
  is_pre_collecting?: boolean;
  credits_total_available?: number;
  registration_type?: RegistrationTypeEnum;
  registration_category?: ValueOf<typeof RegistrationCategory>;
  password?: string | null;
  password_encrypted?: string | null;
  password_metadata?: string | null;
  sales_tax_id?: string;
  pin?: string | null;
  auto_registered?: boolean | null;
  registration_requested?: string | null;
  registration_completed?: string | null;
  deregistration_requested?: string | null;
  deregistration_completed?: string | null;
  filing_website_url?: string;
  do_not_file?: boolean;
  /** Admin Past Due Returns checkbox; persisted as create_back_filing. */
  create_back_filing?: boolean;
  has_physical_presence?: boolean;
  physical_presence_address?: string | null;
  oss_member_state_of_identification_code?: string | null;
  oss_type?: OSSScheme | null;
  /** Alabama and other state-specific jurisdiction JSON (credential values may be redacted). */
  jurisdiction_specific_fields?: Record<string, unknown> | null;
  /** Pending scheduled frequency (KA-801); null when none. */
  scheduled_filing_frequency?: ValueOf<typeof FilingFrequency> | null;
  /** Effective date for pending scheduled frequency (YYYY-MM-DD); null when none. */
  filing_frequency_effective_date?: string | null;
  /**
   * Fiscal-year anchor: the month (1-12) each filing period ends when offset from the
   * calendar. Null = calendar-aligned. Only meaningful for HI quarterly/semiannual or BC
   * quarterly registrations.
   */
  period_end_month?: number | null;
  /** Admin: Kintsugi Mail address is on file with the state for this registration. */
  kintsugi_mail_on_file?: boolean;
  /** Date Tax Ops marked mail on file; null when not on file. Server-set on toggle. */
  kintsugi_mail_confirmed_at?: string | null;
};

export type FrequencyChangeMode = 'immediate' | 'schedule' | 'cancel_schedule';

export type RegistrationFrequencyChangePayload = {
  registration_ids: string[];
  mode: FrequencyChangeMode;
  new_filing_frequency?: ValueOf<typeof FilingFrequency>;
  effective_date?: string;
  /**
   * Fiscal-year anchor, immediate/schedule only. A month (1-12) sets the offset; explicit
   * null reverts to the calendar grid. Omit the key entirely to leave the current anchor
   * unchanged — the backend distinguishes omit from null.
   */
  period_end_month?: number | null;
};

export type RegistrationFrequencyChangeItemResult = {
  registration_id: string;
  success: boolean;
  error: string | null;
};

export type RegistrationFrequencyChangeResponse = {
  results: RegistrationFrequencyChangeItemResult[];
  total_succeeded: number;
  total_failed: number;
  total_requested: number;
};

export interface RegistrationTaskData extends Registration {
  organization_name: string;
  organization_id: string;
  assignee?: string;
}

export type UpdateRegistrationPayload = {
  registration_date?: string | null;
  registration_email?: string | null;
  registration_requested?: string | null;
  registration_completed?: string | null;
  deregistration_requested?: string | null;
  deregistration_completed?: string | null;
  auto_registered?: boolean | null;
  registrations_regime?: RegistrationsRegimeEnum | null;
  change_regime_status?: ChangeRegimeStatusEnum | null;
  third_party_enabled?: boolean | null;
  do_not_file?: boolean;
  create_back_filing?: boolean;
  marked_collecting?: boolean | null;
  status?: ValueOf<typeof RegistrationStatus>;
  country_code?: ValueOf<typeof CountryCode>;
  state_code?: string;
  state_name?: string | null;
  filing_frequency?: ValueOf<typeof FilingFrequency>;
  filing_days?: number;
  username?: string | null;
  comment?: string | null;
  create_filings_from?: string | null;
  initial_sync?: boolean | null;
  amount_fees?: string;
  vda?: boolean | null;
  imported?: boolean | null;
  tax_id?: string | null;
  sst_import?: boolean | null;
  oss_type?: string | null;
  oss_member_state_of_identification_code?: string | null;
  marked_collecting_date?: string | null;
  needs_mark_as_collecting?: boolean | null;
  id?: string;
  credits_total_available?: string;
  registration_category?: ValueOf<typeof RegistrationCategory>;
  password_encrypted?: string | null;
  has_all_credentials?: boolean;
  password_plain_text?: string | null;
  password_metadata_plain_text?: string | null;
  pin_plain_text?: string | null;
  has_physical_presence?: boolean | null;
  physical_presence_address?: string | null;
  /** Admin: set or clear Kintsugi Mail on file with the state; server sets confirmed_at. */
  kintsugi_mail_on_file?: boolean | null;
};

/** Row from GET /admin/registrations/deletion-eligible */
export interface RegistrationDeletionEligibleFiling {
  id: string;
  status: string;
}

export interface RegistrationJurisdictionOption {
  country_code: string;
  state_code: string;
  state_name: string;
}

/** Lightweight row from GET /admin/registrations/filing-eligible */
export interface RegistrationFilingEligible {
  id: string;
  country_code: string;
  state_code: string;
  state_name: string;
  registration_date: string | null;
  status: ValueOf<typeof RegistrationStatus>;
  filing_frequency?: ValueOf<typeof FilingFrequency> | null;
  create_filings_from?: string | null;
}

export interface RegistrationDeletionEligible {
  id: string;
  jurisdiction: RegistrationJurisdictionOption;
  status: string;
  filings_count_to_delete: number;
  filings: RegistrationDeletionEligibleFiling[];
}
